import {
  AlertCircle,
  Bluetooth,
  Calendar,
  CheckCircle,
  Code,
  Cpu,
  FileText,
  Globe,
  Layers,
  Layout,
  LifeBuoy,
  MapPin,
  PenTool,
  PlaySquare,
  RefreshCw,
  Search,
  Server,
  Shield,
  ShieldCheck,
  ShoppingBag,
  Smartphone,
  Target,
  TrendingUp,
  Users,
  Zap,
} from "lucide-react";

import type { AboutUsCopy } from "@components/bluetooth-landing/AboutUs";
import type { FAQCopy } from "@components/bluetooth-landing/FAQ";
import type { FooterCopy } from "@components/bluetooth-landing/Footer";
import type { HeroCopy } from "@components/bluetooth-landing/Hero";
import type { NavbarCopy } from "@components/bluetooth-landing/Navbar";
import type { ProcessCopy } from "@components/bluetooth-landing/Process";
import type { ServicesCopy } from "@components/bluetooth-landing/Services";
import type { TargetAudienceCopy } from "@components/bluetooth-landing/TargetAudience";
import type { ValuePropositionCopy } from "@components/bluetooth-landing/ValueProposition";
import { faqsEn } from "@data/flutter-landing/faq";

export const title =
  "Flutter App Development Company for Bluetooth Devices | Blues Brackets";
export const description =
  "Flutter app development company specializing in Bluetooth LE device companion apps. We build cross-platform iOS and Android apps with Flutter for hardware manufacturers across Europe.";

export const structuredData = [
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Blues Brackets",
    "url": "https://bluesbrackets.com",
    "logo": "https://bluesbrackets.com/favicon-dark.png",
    description,
    "areaServed": "EU",
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "sales",
      "email": "hello@bluesbrackets.com",
      "availableLanguage": ["Polish", "English"],
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Flutter app development for Bluetooth devices",
    "provider": {
      "@type": "Organization",
      "name": "Blues Brackets",
    },
    description,
    "areaServed": "EU",
    "serviceType": "Flutter mobile app development",
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqsEn.map(faq => ({
      "@type": "Question",
      "name": faq.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.a,
      },
    })),
  },
];

export const navbarCopy: NavbarCopy = {
  links: {
    targetAudience: "Who it is for",
    values: "Why Flutter",
    services: "Services",
    process: "Process",
    about: "About us",
    faq: "FAQ",
  },
  cta: "Estimate your project",
};

export const heroCopy: HeroCopy = {
  header: "Flutter app development company",
  headerTail: "for Bluetooth devices",
  description:
    "One codebase. iOS and Android. Deep Bluetooth LE expertise. We help hardware manufacturers build stable, polished Flutter apps their customers actually want to use.",
  cta: {
    text: "Free consultation",
    href: "#kontakt",
  },
  secondaryCta: {
    text: "See our process",
    href: "#proces",
  },
  benefits: [
    { text: "Flutter specialists", Icon: CheckCircle },
    { text: "Cross-platform iOS & Android", Icon: CheckCircle },
    { text: "EU GDPR ready", Icon: CheckCircle },
  ],
};

export const targetAudienceCopy: TargetAudienceCopy = {
  header: {
    h2: "Who we work with",
    h3: "Who are our Flutter development services for?",
    p: "We help companies that need a Flutter companion app for their Bluetooth hardware. Does one of these situations sound familiar?",
  },
  points: [
    {
      Icon: Cpu,
      title: "You manufacture or import Bluetooth devices",
      description:
        "Your hardware is ready and you need a Flutter app to unlock its full value for end users.",
    },
    {
      Icon: Smartphone,
      title: "You want one app for both iOS and Android",
      description:
        "Maintaining two native codebases is expensive. Flutter gives you a single codebase with native performance on both platforms.",
    },
    {
      Icon: AlertCircle,
      title: "Your current app is underperforming",
      description:
        "It looks dated, drops Bluetooth connections, or is hard to use. We can rewrite it in Flutter and get it right.",
    },
    {
      Icon: RefreshCw,
      title: "You want to migrate from React Native or native",
      description:
        "We analyse your existing app and codebase, then plan a Flutter migration that keeps what works and improves the rest.",
    },
    {
      Icon: Target,
      title: "You need to hire Flutter app developers",
      description:
        "You have an in-house team but need additional Flutter capacity or a specialist who understands BLE. We work directly with your engineers.",
    },
  ],
  cta: {
    h4: "Sounds like your project?",
    text: "Tell us about your device and app requirements. We will help you find the right scope and approach.",
    href: "#kontakt",
    buttonText: "Book a free consultation",
  },
};

export const valuePropositionCopy: ValuePropositionCopy = {
  header: {
    h2: "Why us",
    h3: "Why choose us as your Flutter development company?",
    p: "We combine Flutter expertise with hands-on Bluetooth LE experience. You get a team that knows both sides of the stack — the app and the hardware.",
    imageAlt: "Flutter app development and Bluetooth code",
  },
  values: [
    {
      Icon: Code,
      title: "Flutter is our primary stack",
      description:
        "We build Flutter apps day to day. We know the framework's strengths, its BLE ecosystem, and the platform-specific details that make the difference between a good app and a great one.",
    },
    {
      Icon: Bluetooth,
      title: "Bluetooth LE is our specialty",
      description:
        "Deep BLE knowledge sets us apart from generalist Flutter agencies. We have hands-on experience with BLE GATT profiles, Auracast, connection stability, and low-power optimization.",
    },
    {
      Icon: Layers,
      title: "One codebase, two platforms",
      description:
        "A shared Flutter codebase means lower development cost, faster iteration, and a consistent user experience on iOS and Android — with no feature gap between platforms.",
    },
    {
      Icon: Layout,
      title: "UX comes first",
      description:
        "Hardware apps often have complex workflows. Our UX designer simplifies device interaction and makes the app feel effortless — so your customers keep coming back.",
    },
    {
      Icon: Globe,
      title: "Built for the European market",
      description:
        "GDPR compliance, EU data storage, English and Polish support. We understand the regulatory and cultural requirements for launching a product across Europe.",
    },
  ],
};

export const servicesCopy: ServicesCopy = {
  header: {
    h2: "Services",
    h3: "Flutter mobile app development services",
    p: "From discovery workshops and UX design to store release and post-launch maintenance. We cover the full lifecycle of your Flutter Bluetooth app.",
  },
  services: [
    {
      Icon: PenTool,
      title: "Product workshops",
      description:
        "We turn your requirements into a clear product brief, identify the core features, and split the project into manageable stages with realistic timelines.",
    },
    {
      Icon: Search,
      title: "Bluetooth device analysis",
      description:
        "We analyze the device's GATT profile and communication protocol. If documentation is missing, we recover it through protocol analysis before development begins.",
    },
    {
      Icon: Layers,
      title: "UX/UI design",
      description:
        "We design intuitive Flutter interfaces that make Bluetooth device control feel simple. Every screen is optimized for usability and implementation readiness.",
    },
    {
      Icon: Smartphone,
      title: "Flutter application development",
      description:
        "We build cross-platform Flutter apps with full BLE integration — connection management, GATT communication, background handling, and real-device testing on both iOS and Android.",
    },
    {
      Icon: Server,
      title: "Backend and API integration",
      description:
        "We build backend services, REST APIs, Firebase integrations, admin panels, user account management, and analytics — when your product needs more than just the app.",
    },
    {
      Icon: PlaySquare,
      title: "App Store and Google Play release",
      description:
        "We prepare your Flutter app for review, handle the submission process on both stores, and help you meet the formal and technical requirements for a successful launch.",
    },
    {
      Icon: LifeBuoy,
      title: "Post-launch support",
      description:
        "We monitor performance, ship updates for new iOS and Android versions, fix issues fast, and continue adding features as your product grows.",
    },
    {
      Icon: Users,
      title: "Flutter developer hire",
      description:
        "Need dedicated Flutter capacity? We offer team augmentation for companies that want to hire Flutter app developers without the overhead of full recruitment.",
    },
  ],
  cta: {
    h4: "Not sure which services you need?",
    p: "Tell us about your project and device. We will recommend the right scope and give you a realistic estimate.",
    button: {
      text: "Book a free consultation",
      href: "#kontakt",
    },
  },
};

export const processCopy: ProcessCopy = {
  header: {
    h2: "Our process",
    h3: "How your Flutter app gets built",
    p: "Defined stages, clear communication, and no surprises. Here is how we work from your first message to launch day.",
  },
  stepNumber: "Step",
  steps: [
    {
      Icon: Calendar,
      title: "Free online meeting",
      duration: "within a few hours",
      description:
        "A 30-minute call with a Flutter and BLE expert. You get a summary and an initial project estimate.",
    },
    {
      Icon: FileText,
      title: "Product workshops",
      duration: "within a few days",
      description:
        "After a 2-hour online workshop you receive a PDF report, a detailed estimate, and a delivery proposal.",
    },
    {
      Icon: Search,
      title: "Bluetooth device analysis",
      duration: "1–2 weeks",
      description:
        "We test the device, document its BLE profile, and confirm it meets the project requirements before writing a line of Flutter code.",
    },
    {
      Icon: PenTool,
      title: "Contract signing",
      duration: "schedule agreement",
      description:
        "We agree on start and end dates with a clear commitment to the timeline.",
    },
    {
      Icon: Code,
      title: "Stage-by-stage Flutter development",
      duration: "min. 2–3 months",
      description:
        "UX mockups, Flutter development, BLE integration, and testing. You follow progress and test demo builds throughout.",
    },
    {
      Icon: ShoppingBag,
      title: "Store release",
      duration: "min. 2 weeks",
      description:
        "We handle the Apple and Google review processes and publish the Flutter app on both stores.",
    },
    {
      Icon: CheckCircle,
      title: "Handover and support",
      duration: "ongoing",
      description:
        "You receive the full source code, or we start a continuous support and maintenance service.",
    },
    {
      Icon: Zap,
      title: "Further development",
      duration: "future versions",
      description:
        "New features, additional Bluetooth devices, analytics, marketing integrations — your Flutter app is ready to grow.",
    },
  ],
  cta: {
    href: "#kontakt",
    text: "Start building your Flutter app",
  },
};

export const aboutUsCopy: AboutUsCopy = {
  header: {
    h2: "About us",
    h3: "A Flutter development agency built on hardware expertise",
    p1: "We are a small team from Poland combining deep experience in Flutter, Bluetooth LE, and mobile product development.",
    p2: "Michał, Tomasz, and Kuba lead Flutter development and ensure code quality after launch, while Mateusz designs the UX that makes complex hardware interactions feel simple.",
    p3: "GDPR compliant",
    p4: "Data in the EU",
    Icon: ShieldCheck,
    imageAlt: "Flutter development team working on a Bluetooth app",
  },
  values: [
    {
      Icon: Users,
      title: "You work directly with our Flutter developers",
      description:
        "No account managers or outsourcing layers. You get technical partners who understand your product.",
    },
    {
      Icon: MapPin,
      title: "We understand the European market",
      description:
        "We know how to build and launch Flutter apps that meet GDPR requirements and EU user expectations.",
    },
    {
      Icon: Shield,
      title: "Security and privacy by default",
      description:
        "All user data is stored in the EU. We apply secure coding practices and privacy-first design from day one.",
    },
  ],
  cta: {
    text: "Contact us",
    href: "#kontakt",
  },
};

export const faqCopy: FAQCopy = {
  header: {
    h2: "FAQ",
    h3: "Frequently asked questions about Flutter development",
  },
  faqs: faqsEn,
};

export const footerCopy: FooterCopy = {
  header: {
    h2: "Looking for a Flutter development company?",
    p: "You are in the right place. Book a free consultation, tell us about your Bluetooth device, and get an initial estimate for your Flutter app.",
  },
  cta: {
    href: "#kontakt",
    text: "Book a free consultation",
    p: "We are a Flutter app development agency specializing in Bluetooth LE companion apps. We build cross-platform iOS and Android apps for hardware manufacturers across Europe.",
    email: "hello@bluesbrackets.com",
    phone: "+48 535 462 678",
  },
  links: {
    h4: "Quick links",
    links: [
      { href: "#dla-kogo", text: "Who we work with" },
      { href: "#uslugi", text: "Services" },
      { href: "#proces", text: "Process" },
      { href: "#faq", text: "FAQ" },
      { href: "/mobile-app-with-bluetooth", text: "Mobile App with Bluetooth" },
      { href: "/ibeacon-app-development/", text: "iBeacon App Development" },
    ],
  },
  knowledgeBase: {
    h4: "Knowledge base",
    links: [
      {
        href: "/blogs/is_bluetooth_le_right_for_your_product",
        text: "Is Bluetooth LE the right technology for your product or MVP?",
      },
      {
        href: "/blogs/bluetooth_le_product_architecture",
        text: "Bluetooth LE IoT product architecture and development",
      },
      {
        href: "/blogs/ble_mobile_application_support",
        text: "BLE support on the mobile application side",
      },
      {
        href: "/blogs/ble_embedded_development_platforms",
        text: "Best platforms for Bluetooth LE product development",
      },
      {
        href: "/blogs/auracast_mcu",
        text: "ESP32 Auracast: Does It Work? Best MCUs for LE Audio",
      },
      {
        href: "/blogs/ble_hardware_2026",
        text: "Best hardware for Bluetooth Low Energy BLE development in 2026",
      },
    ],
  },
  copyright: "bluesBrackets. All rights reserved.",
  privacyPolicy: "Privacy Policy",
  privacyPolicyHref: "/privacy-policy",
  copiedLabel: "Copied to clipboard",
  contactForm: {
    heading: "Contact us",
    fields: {
      name: {
        label: "Full name",
        placeholder: "John Smith",
        requiredError: "Enter your full name",
      },
      email: {
        label: "Email address",
        placeholder: "john@company.com",
        requiredError: "Enter your email address",
        invalidError: "Enter a valid email address",
      },
      topic: {
        label: "Project description",
        placeholder:
          "Briefly describe your Bluetooth device and app requirements...",
        requiredError: "Describe your project",
        maxLengthError: "The description can be up to 2000 characters long",
      },
    },
    submit: "Send inquiry",
    submitting: "Sending...",
    successMessage: "Message sent!",
    errorMessage: "Could not send the message :(",
    responseTime: "We reply within 24 hours on business days.",
    emailSubjectPrefix: "Full name",
  },
};
